/**
 * Track Store
 * 
 * Manages Tracks - the durable middle object between Cycle and Loop/Iteration/Pool.
 * 
 * A Track defines a comparability lane partitioned by:
 * - cycle_id: which cycle this track belongs to
 * - scope: organizational partition (org_unit or default)
 * - required_item_category: item type filter
 * - required_altitude_level: altitude level filter
 * 
 * Per Build Contract: Database is the system of record.
 * All state changes are persisted to Supabase and can be reconstructed on refresh.
 */

import { create } from 'zustand';
import { v4 as uuid } from 'uuid';
import { getSafeSupabaseClient, isSupabaseAvailable, getCurrentWorkspaceId } from '../../lib/supabaseSync';
import { useAuditStore } from './auditStore';
import {
  type Track,
  type TrackId,
  type TrackRow,
  type CreateTrackInput,
  type UpdateTrackInput,
  trackRowToDomain,
  UNSCOPED_CATEGORY,
  UNSCOPED_ALTITUDE,
  mergeTrackOwnerIntoMetadata,
  stripSessionPolicyKeysFromTrackMetadata,
  SESSION_POLICY_TRACK_METADATA_KEYS,
} from '../../types/prioritization/track';
import {
  useSessionPolicyStore,
  decorateTrackWithPolicy,
} from './sessionPolicyStore';
import { checkPolicyForTrackCreate } from '../../lib/authorization/storeEnforcement';
import { getReasonMessage } from '../../lib/authorization/reasonMessages';
import { isUuid } from '../../lib/isUuid';
import type { Json } from '../../types/database.generated';

interface TrackStore {
  // State
  tracks: Track[];
  isLoading: boolean;
  error: string | null;
  /**
   * Where the workspace-wide read (fetchAllTracks) stands. Kept apart from
   * isLoading, which every track read flips, so the Tracks list can say
   * "could not load" with Retry instead of spinning (audit #37).
   */
  allTracksStatus: 'idle' | 'loading' | 'loaded' | 'error';
  allTracksError: string | null;
  
  // CRUD Actions
  createTrack: (input: CreateTrackInput) => Promise<Track | null>;
  updateTrack: (trackId: TrackId, updates: UpdateTrackInput) => Promise<boolean>;
  deleteTrack: (trackId: TrackId) => Promise<boolean>;
  /**
   * Transition a standalone (Unbound) Track Instance to Cycle-Bound state.
   * Atomically sets cycle_id on the track and cascades to owned sessions.
   */
  bindTrackToCycle: (trackId: TrackId, cycleId: string) => Promise<boolean>;
  
  // Selectors
  getTrack: (trackId: TrackId) => Track | undefined;
  getTracksForCycle: (cycleId: string) => Track[];
  getDefaultTrack: (cycleId: string) => Track | undefined;
  getActiveTracksForCycle: (cycleId: string) => Track[];
  /** Returns all tracks that are not tied to any cycle. */
  getStandaloneTracks: () => Track[];
  /** Returns active standalone tracks only. */
  getActiveStandaloneTracks: () => Track[];
  
  // Status transitions
  markAccepted: (trackId: TrackId) => Promise<boolean>;
  markRejected: (trackId: TrackId) => Promise<boolean>;
  archiveTrack: (trackId: TrackId) => Promise<boolean>;
  
  // Sync
  fetchTracksForCycle: (cycleId: string) => Promise<void>;
  fetchAllTracks: () => Promise<void>;
  /** Fetch standalone (cycle-independent) tracks for the current workspace. */
  fetchStandaloneTracks: () => Promise<void>;
  
  // Utilities
  clearError: () => void;
  getNextTrackName: (cycleId: string | null, baseName: string) => string;
  /**
   * Phase C-2: re-apply the cached session_policies 'track' row onto the
   * local track (default* fields + metadata allowSession* flags). Call after
   * sessionPolicyStore.upsertPolicy so the UI reflects the save.
   */
  applyPolicyToTrackLocal: (trackId: TrackId) => void;
}

/**
 * Phase C-2 decoration (canon 28 §6, migration 598): Session Policy data
 * lives on session_policies ('track' scope). After mapping DB rows to domain
 * objects, merge the policy values onto the legacy domain fields so every
 * existing reader keeps working. One query per fetch batch.
 */
/** A track row with its session baseline: the same columns on every track read. */
const TRACK_SELECT =
  '*, session_baselines(criteria_locked_at, values_converged_at, pool_locked_at, value_collection_id, value_collection_snapshot_id, value_source_selected_by, value_source_selected_at, pool_amended_at, pool_amendment_note)';

/** The Tracks list waits this long, then says it could not load and offers Retry. */
const ALL_TRACKS_TIMEOUT_MS = 20_000;

/** Only the newest fetchAllTracks may write its answer; an older one that ends later is dropped. */
let allTracksRequestSeq = 0;

function allTracksSignal(): AbortSignal | undefined {
  return typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function'
    ? AbortSignal.timeout(ALL_TRACKS_TIMEOUT_MS)
    : undefined;
}

async function decorateTracksWithPolicies<T extends Track>(tracks: T[]): Promise<T[]> {
  if (tracks.length === 0) return tracks;
  const policies = await useSessionPolicyStore
    .getState()
    .fetchPoliciesForScopes('track', tracks.map((t) => t.id));
  return tracks.map((t) => decorateTrackWithPolicy(t, policies.get(t.id)));
}

export const useTrackStore = create<TrackStore>()((set, get) => ({
  tracks: [],
  isLoading: false,
  error: null,
  allTracksStatus: 'idle',
  allTracksError: null,
  
  // ============================================================================
  // CREATE
  // ============================================================================
  
  createTrack: async (input: CreateTrackInput): Promise<Track | null> => {
    if (!isSupabaseAvailable()) {
      set({ error: 'Supabase not available' });
      return null;
    }
    
    const supabase = getSafeSupabaseClient();
    if (!supabase) {
      set({ error: 'Supabase client not available' });
      return null;
    }

    // §2.3a: tracks.create is an authority-creating action — verify against
    // the DB directly rather than trusting Zustand's in-memory capability
    // cache, which can be stale or empty at call time.
    const policyDecision = await checkPolicyForTrackCreate();
    if (!policyDecision.allowed) {
      const reasonCode = policyDecision.reasons[0]?.code || 'MISSING_CAPABILITY';
      console.warn('[TrackStore] Policy denied for tracks.create:', reasonCode);
      set({ error: getReasonMessage(reasonCode) });
      return null;
    }

    const isStandalone = input.isStandalone ?? !input.cycleId;

    try {
      const now = new Date().toISOString();
      const ownerIdTrimmed = input.ownerUserId?.trim() || '';
      // Phase C-2: allowSession* flags are Policy content (session_policies);
      // never persist them to tracks.metadata.
      const baseMetadata: Record<string, unknown> =
        stripSessionPolicyKeysFromTrackMetadata({ ...(input.metadata ?? {}) }) ?? {};
      const metadata =
        ownerIdTrimmed.length > 0
          ? mergeTrackOwnerIntoMetadata(baseMetadata, ownerIdTrimmed)
          : Object.keys(baseMetadata).length > 0
          ? baseMetadata
          : null;

      const workspaceId = getCurrentWorkspaceId();

      const newTrackRow: Partial<TrackRow> = {
        id: uuid(),
        cycle_id: input.cycleId ?? null,
        is_standalone: isStandalone,
        // Always set workspace_id so RLS workspace-scoped policies apply.
        // Migration 353 requires workspace_id = ANY(user_workspace_ids()) for
        // INSERT; a NULL workspace_id on a cycle-linked track would fail that check.
        workspace_id: workspaceId ?? null,
        name: input.name,
        description: input.description || null,
        scope_type: input.scopeType || 'default',
        scope_entity_id: input.scopeEntityId || null,
        scope_label: input.scopeLabel || 'Default',
        // The reorg worklist and mig-729 integrity checks read org_unit_id,
        // not scope_entity_id — keep both in sync whenever the scope is an
        // org unit so app-created org-scoped tracks stay visible to a reorg.
        org_unit_id:
          (input.scopeType || 'default') === 'org_unit' && isUuid(input.scopeEntityId)
            ? input.scopeEntityId
            : null,
        required_item_category: input.requiredItemCategory || UNSCOPED_CATEGORY,
        required_altitude_level: input.requiredAltitudeLevel ?? UNSCOPED_ALTITUDE,
        status: 'active',
        metadata,
        created_at: now,
        updated_at: now,
      };
      
      // Mig 762: one governed write (create_track) — the row plus a
      // track_created governance event. The server re-checks TRACK_CREATE,
      // that the owner and org unit belong to the workspace, and notifies the
      // owner and the cycle owner (trigger), for every write path.
      const { data: res, error: rpcErr } = await supabase.rpc('create_track', {
        p_track: newTrackRow as unknown as Json,
      });
      const envelope = (res ?? null) as {
        success?: boolean;
        error?: string;
        code?: string;
        track_id?: string;
        existing_track_id?: string | null;
      } | null;

      if (rpcErr || !envelope?.success) {
        if (envelope?.code === 'duplicate' && input.cycleId) {
          await get().fetchTracksForCycle(input.cycleId);
          const existing =
            (envelope.existing_track_id && get().getTrack(envelope.existing_track_id)) ||
            get().getTracksForCycle(input.cycleId)[0];
          if (existing) {
            console.log('[TrackStore] Track already exists for cycle, returning existing:', existing.id);
            return existing;
          }
        }
        const message = rpcErr?.message || envelope?.error || 'The track could not be created.';
        console.error('[TrackStore] create_track refused:', rpcErr ?? envelope);
        set({ error: message });
        return null;
      }

      const { data, error } = await supabase
        .from('tracks')
        .select(TRACK_SELECT)
        .eq('id', envelope.track_id ?? newTrackRow.id)
        .single();
      if (error || !data) {
        // Created, but the read-back failed: say so rather than pretend.
        const message = `The track was created but could not be loaded: ${error?.message ?? 'not found'}. Refresh to see it.`;
        set({ error: message });
        return null;
      }

      const track = decorateTrackWithPolicy(
        trackRowToDomain(data as TrackRow),
        useSessionPolicyStore.getState().getPolicy('track', (data as TrackRow).id)
      );

      set(state => ({
        tracks: [...state.tracks, track],
        error: null,
      }));
      
      console.log('[TrackStore] Created track:', track.id, track.name, isStandalone ? '(standalone)' : `(cycle: ${input.cycleId})`);

      // The owner notice is server-side now (trg_notify_track_created, mig 762).

      if (!isStandalone && input.cycleId) {
        // The cycle's template seed (criteria, scales) was given to this track
        // by the server as it was made (migration 1040), once.

        // Re-fetch tracks for the cycle to ensure local state matches DB
        try {
          await get().fetchTracksForCycle(input.cycleId);
        } catch (e) { console.warn('[TrackStore] Post-create re-fetch failed:', e); }
      }

      return track;
    } catch (err) {
      console.error('[TrackStore] Create exception:', err);
      set({ error: String(err) });
      return null;
    }
  },
  
  // ============================================================================
  // UPDATE
  // ============================================================================
  
  updateTrack: async (trackId: TrackId, updates: UpdateTrackInput): Promise<boolean> => {
    if (!isSupabaseAvailable()) {
      set({ error: 'Supabase not available' });
      return false;
    }

    // Stage 6: Set Official Track Result requires track.set_official_result on the track's cycle (configurable from Settings)
    if (updates.officialSessionId !== undefined) {
      const track = get().getTrack(trackId);
      if (track?.cycleId) {
        const { useDegapProcessStore } = await import('./degapProcessStore');
        const { checkPolicyForCycle } = await import('../../lib/authorization/storeEnforcement');
        const cycle = useDegapProcessStore.getState().getCycle(track.cycleId);
        if (cycle) {
          const decision = await checkPolicyForCycle('track.set_official_result', track.cycleId, cycle as unknown as Record<string, unknown>);
          if (!decision.allowed) {
            const reasonCode = decision.reasons[0]?.code || 'DENIED';
            set({ error: `Cannot choose the track's result: ${reasonCode}` });
            if (typeof window !== 'undefined') {
              window.dispatchEvent(new CustomEvent('authorization-denied', {
                detail: { store: 'track', action: 'setOfficialResult', message: `Cannot choose the track's result: ${reasonCode}`, decision },
              }));
            }
            return false;
          }
        }
      }
    }
    
    const supabase = getSafeSupabaseClient();
    if (!supabase) return false;

    try {
      const updateRow: Partial<TrackRow> = {
        updated_at: new Date().toISOString(),
      };
      
      if (updates.name !== undefined) updateRow.name = updates.name;
      if (updates.description !== undefined) updateRow.description = updates.description;
      if (updates.scopeLabel !== undefined) updateRow.scope_label = updates.scopeLabel;
      if (updates.status !== undefined) updateRow.status = updates.status;
      // Phase C-2: strip decorated allowSession* flags before persisting —
      // policy content lives on session_policies, not tracks.metadata.
      if (updates.metadata !== undefined) updateRow.metadata = stripSessionPolicyKeysFromTrackMetadata(updates.metadata);
      if (updates.requiredItemCategory !== undefined) updateRow.required_item_category = updates.requiredItemCategory;
      if (updates.requiredAltitudeLevel !== undefined) updateRow.required_altitude_level = updates.requiredAltitudeLevel;
      if (updates.officialSessionId !== undefined) updateRow.official_session_id = updates.officialSessionId === null || updates.officialSessionId === '' ? null : updates.officialSessionId;
      if (updates.officialSessionReportId !== undefined) updateRow.official_session_report_id = updates.officialSessionReportId === null || updates.officialSessionReportId === '' ? null : updates.officialSessionReportId;
      if (updates.resultResolutionOverride !== undefined) updateRow.result_resolution_override = updates.resultResolutionOverride === null || updates.resultResolutionOverride === '' ? null : updates.resultResolutionOverride;
      if (updates.readinessDefinitionId !== undefined) updateRow.readiness_definition_id = updates.readinessDefinitionId ?? null;
      
      const { data, error } = await supabase
        .from('tracks')
        .update(updateRow)
        .eq('id', trackId)
        .select(TRACK_SELECT)
        .single();
      
      if (error) {
        console.error('[TrackStore] Update error:', error);
        set({ error: error.message });
        return false;
      }
      
      const updatedTrack = decorateTrackWithPolicy(
        trackRowToDomain(data as TrackRow),
        useSessionPolicyStore.getState().getPolicy('track', trackId)
      );

      set(state => ({
        tracks: state.tracks.map(t => t.id === trackId ? updatedTrack : t),
        error: null,
      }));

      // Owner-change notices are server-side (trg_notify_track_owner_changed, mig 762).

      if (updates.officialSessionId !== undefined) {
        const auditResult = await useAuditStore.getState().logStage6GovernanceDurable({
          type: 'official_result_set',
          trackId,
          sessionId: updates.officialSessionId ?? undefined,
          actorId: '',
          actorRole: 'workspace-admin',
          details: { officialSessionId: updates.officialSessionId },
        });
        if (!auditResult.auditSaved && typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('store-error', {
            detail: { store: 'track', action: 'setOfficialResult', message: `Governance event could not be saved: ${auditResult.error ?? 'unknown'}` },
          }));
        }
      }
      
      console.log('[TrackStore] Updated track:', trackId);
      return true;
    } catch (err) {
      console.error('[TrackStore] Update exception:', err);
      set({ error: String(err) });
      return false;
    }
  },
  
  // ============================================================================
  // DELETE
  // ============================================================================
  
  deleteTrack: async (trackId: TrackId): Promise<boolean> => {
    if (!isSupabaseAvailable()) {
      set({ error: 'Supabase not available' });
      return false;
    }
    
    const supabase = getSafeSupabaseClient();
    if (!supabase) return false;
    
    const track = get().getTrack(trackId);
    if (!track) {
      set({ error: 'Track not found' });
      return false;
    }
    
    // Prevent deletion of default tracks
    if (track.scopeType === 'default' && 
        track.requiredItemCategory === UNSCOPED_CATEGORY &&
        track.requiredAltitudeLevel === UNSCOPED_ALTITUDE) {
      set({ error: 'Cannot delete the default track for a cycle' });
      return false;
    }

    // G02: block track deletion when active sessions reference this track. Canon §G02.
    const { data: activeSessions, error: sessionCheckErr } = await supabase
      .from('prioritization_sessions')
      .select('id')
      .eq('track_id', trackId)
      .in('state', ['DRAFT', 'CRITERIA_FINALIZED', 'RUN_COMPLETE'])
      .limit(1);

    if (sessionCheckErr) {
      console.warn('[TrackStore] G02: could not check for active sessions:', sessionCheckErr.message);
    } else if (activeSessions && activeSessions.length > 0) {
      const msg = 'Cannot delete track: active sessions are using it. Archive or reassign them first.';
      set({ error: msg });
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('track-deletion-blocked', {
          detail: { trackId, message: msg },
        }));
      }
      return false;
    }

    try {
      const { error } = await supabase
        .from('tracks')
        .delete()
        .eq('id', trackId);
      
      if (error) {
        console.error('[TrackStore] Delete error:', error);
        set({ error: error.message });
        return false;
      }
      
      set(state => ({
        tracks: state.tracks.filter(t => t.id !== trackId),
        error: null,
      }));
      
      console.log('[TrackStore] Deleted track:', trackId);
      return true;
    } catch (err) {
      console.error('[TrackStore] Delete exception:', err);
      set({ error: String(err) });
      return false;
    }
  },
  
  // ============================================================================
  // BINDING: Unbound → Cycle-Bound transition
  // ============================================================================

  bindTrackToCycle: async (trackId: TrackId, cycleId: string): Promise<boolean> => {
    if (!isSupabaseAvailable()) {
      set({ error: 'Supabase not available' });
      return false;
    }
    const supabase = getSafeSupabaseClient();
    if (!supabase) return false;

    let track = get().getTrack(trackId);

    // If local state doesn't know the track, or local state has stale isStandalone=false,
    // fetch directly from DB before checking.
    if (!track || !track.isStandalone) {
      const { data: freshRow, error: fetchErr } = await supabase
        .from('tracks')
        .select(TRACK_SELECT)
        .eq('id', trackId)
        .single();

      if (fetchErr || !freshRow) {
        set({ error: 'Track not found in database' });
        return false;
      }

      const freshTrack = decorateTrackWithPolicy(
        trackRowToDomain(freshRow as TrackRow),
        useSessionPolicyStore.getState().getPolicy('track', trackId)
      );
      // Update local state with fresh data
      set(state => ({
        tracks: state.tracks.some(t => t.id === trackId)
          ? state.tracks.map(t => t.id === trackId ? freshTrack : t)
          : [...state.tracks, freshTrack],
      }));

      if (!freshTrack.isStandalone) {
        set({ error: 'Track is already bound to a cycle — refresh the page if this looks wrong' });
        return false;
      }

      track = freshTrack;
    }

    try {
      const { error } = await supabase.rpc('bind_track_to_cycle', {
        p_track_id: trackId,
        p_cycle_id: cycleId,
      });

      if (error) {
        console.error('[TrackStore] bindTrackToCycle error:', error);
        set({ error: error.message });
        return false;
      }

      // Update local state optimistically
      set(state => ({
        tracks: state.tracks.map(t =>
          t.id === trackId
            ? { ...t, cycleId, isStandalone: false, updatedAt: new Date() }
            : t
        ),
        error: null,
      }));

      console.log('[TrackStore] Bound track', trackId, 'to cycle', cycleId);
      return true;
    } catch (err) {
      console.error('[TrackStore] bindTrackToCycle exception:', err);
      set({ error: String(err) });
      return false;
    }
  },

  // ============================================================================
  // SELECTORS
  // ============================================================================

  getTrack: (trackId: TrackId): Track | undefined => {
    return get().tracks.find(t => t.id === trackId);
  },
  
  getTracksForCycle: (cycleId: string): Track[] => {
    return get().tracks
      .filter(t => t.cycleId === cycleId)
      .sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
  },
  
  getDefaultTrack: (cycleId: string): Track | undefined => {
    const forCycle = get().tracks
      .filter(t => t.cycleId === cycleId)
      .sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
    return forCycle.find(t => t.scopeType === 'default') ?? forCycle[0];
  },
  
  getActiveTracksForCycle: (cycleId: string): Track[] => {
    return get().tracks
      .filter(t => t.cycleId === cycleId && t.status === 'active')
      .sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
  },

  getStandaloneTracks: (): Track[] => {
    return get().tracks
      .filter(t => t.isStandalone)
      .sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
  },

  getActiveStandaloneTracks: (): Track[] => {
    return get().tracks
      .filter(t => t.isStandalone && t.status === 'active')
      .sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
  },
  
  // ============================================================================
  // STATUS TRANSITIONS
  // ============================================================================
  
  markAccepted: async (trackId: TrackId): Promise<boolean> => {
    return get().updateTrack(trackId, { status: 'accepted' });
  },
  
  markRejected: async (trackId: TrackId): Promise<boolean> => {
    return get().updateTrack(trackId, { status: 'final_rejected' });
  },
  
  archiveTrack: async (trackId: TrackId): Promise<boolean> => {
    return get().updateTrack(trackId, { status: 'archived' });
  },
  
  // ============================================================================
  // SYNC
  // ============================================================================
  
  fetchTracksForCycle: async (cycleId: string): Promise<void> => {
    if (!isSupabaseAvailable()) {
      console.log('[TrackStore] Supabase not available');
      return;
    }
    
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;
    
    set({ isLoading: true, error: null });
    
    try {
      const { data, error } = await supabase
        .from('tracks')
        .select(TRACK_SELECT)
        .eq('cycle_id', cycleId);
      
      if (error) {
        console.error('[TrackStore] Fetch error:', error);
        set({ isLoading: false, error: error.message });
        return;
      }
      
      const fetchedTracks = await decorateTracksWithPolicies(
        (data as TrackRow[]).map(trackRowToDomain)
      );

      // Merge with existing tracks (preserve tracks from other cycles)
      set(state => {
        const otherTracks = state.tracks.filter(t => t.cycleId !== cycleId);
        return {
          tracks: [...otherTracks, ...fetchedTracks],
          isLoading: false,
          error: null,
        };
      });
      
      console.log('[TrackStore] Loaded', fetchedTracks.length, 'tracks for cycle:', cycleId);
    } catch (err) {
      console.error('[TrackStore] Fetch exception:', err);
      set({ isLoading: false, error: String(err) });
    }
  },
  
  fetchAllTracks: async (): Promise<void> => {
    if (!isSupabaseAvailable()) {
      console.log('[TrackStore] Supabase not available');
      return;
    }

    const supabase = getSafeSupabaseClient();
    if (!supabase) return;

    const workspaceId = getCurrentWorkspaceId();
    const requestId = ++allTracksRequestSeq;
    const isLatest = () => requestId === allTracksRequestSeq;
    const fail = (message: string) => {
      if (!isLatest()) return;
      set({ isLoading: false, error: message, allTracksStatus: 'error', allTracksError: message });
    };

    set({ isLoading: true, error: null, allTracksStatus: 'loading', allTracksError: null });

    try {
      const signal = allTracksSignal();
      let rows: TrackRow[];

      if (workspaceId) {
        // Tracks take their workspace from their cycle. This used to read the
        // workspace's cycle ids first and send them all back in the URL
        // (cycle_id.in.(...)); a workspace with hundreds of cycles made that URL
        // too long and the read failed (staging's walkthrough workspace: 650
        // cycles, a 25 KB query string). Join the cycle instead (one request
        // whatever the size) and read this workspace's standalone tracks
        // beside it. tracks.workspace_id is not used for cycle-bound tracks: it
        // is empty on many older rows.
        const linkedQuery = supabase
          .from('tracks')
          .select(`${TRACK_SELECT}, cycle:degap_cycles!inner(workspace_id)`)
          .eq('cycle.workspace_id', workspaceId)
          .order('created_at', { ascending: true });
        const standaloneQuery = supabase
          .from('tracks')
          .select(TRACK_SELECT)
          .eq('is_standalone', true)
          .eq('workspace_id', workspaceId)
          .order('created_at', { ascending: true });
        const [linked, standalone] = await Promise.all([
          signal ? linkedQuery.abortSignal(signal) : linkedQuery,
          signal ? standaloneQuery.abortSignal(signal) : standaloneQuery,
        ]);
        const queryError = linked.error ?? standalone.error;
        if (queryError) {
          console.error('[TrackStore] Fetch all error:', queryError);
          fail(queryError.message);
          return;
        }
        const byId = new Map<string, TrackRow>();
        for (const row of [...(linked.data ?? []), ...(standalone.data ?? [])] as Array<TrackRow & { cycle?: unknown }>) {
          const { cycle: _cycle, ...track } = row;
          byId.set(track.id, track as TrackRow);
        }
        rows = [...byId.values()].sort((a, b) => String(a.created_at).localeCompare(String(b.created_at)));
      } else {
        const query = supabase
          .from('tracks')
          .select(TRACK_SELECT)
          .order('created_at', { ascending: true });
        const { data, error } = await (signal ? query.abortSignal(signal) : query);
        if (error) {
          console.error('[TrackStore] Fetch all error:', error);
          fail(error.message);
          return;
        }
        rows = (data ?? []) as TrackRow[];
      }

      const fetchedTracks = await decorateTracksWithPolicies(rows.map(trackRowToDomain));
      if (!isLatest()) return;

      set({
        tracks: fetchedTracks,
        isLoading: false,
        error: null,
        allTracksStatus: 'loaded',
        allTracksError: null,
      });

      console.log('[TrackStore] Loaded', fetchedTracks.length, 'tracks for workspace', workspaceId);
    } catch (err) {
      console.error('[TrackStore] Fetch all exception:', err);
      const name = err instanceof Error ? err.name : '';
      fail(
        name === 'TimeoutError' || name === 'AbortError'
          ? `Tracks took longer than ${ALL_TRACKS_TIMEOUT_MS / 1000} seconds to load.`
          : err instanceof Error ? err.message : String(err),
      );
    }
  },

  fetchStandaloneTracks: async (): Promise<void> => {
    if (!isSupabaseAvailable()) return;
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;

    set({ isLoading: true, error: null });

    try {
      const workspaceId = getCurrentWorkspaceId();
      let query = supabase
        .from('tracks')
        .select(TRACK_SELECT)
        .eq('is_standalone', true)
        .order('created_at', { ascending: true });

      // Standalone tracks don't have a workspace_id directly; use a workspace
      // filter via the workspace column if present, otherwise fetch all visible.
      // RLS will restrict to the user's workspaces.
      if (workspaceId) {
        query = query.eq('workspace_id', workspaceId);
      }

      const { data, error } = await query;

      if (error) {
        console.error('[TrackStore] Fetch standalone error:', error);
        set({ isLoading: false, error: error.message });
        return;
      }

      const standaloneTracks = await decorateTracksWithPolicies(
        (data as TrackRow[]).map(trackRowToDomain)
      );

      set(state => {
        // Merge: keep cycle-linked tracks, replace standalone set
        const cycleTracks = state.tracks.filter(t => !t.isStandalone);
        return {
          tracks: [...cycleTracks, ...standaloneTracks],
          isLoading: false,
          error: null,
        };
      });

      console.log('[TrackStore] Loaded', standaloneTracks.length, 'standalone tracks');
    } catch (err) {
      console.error('[TrackStore] Fetch standalone exception:', err);
      set({ isLoading: false, error: String(err) });
    }
  },
  
  // ============================================================================
  // UTILITIES
  // ============================================================================
  
  clearError: () => {
    set({ error: null });
  },

  applyPolicyToTrackLocal: (trackId: TrackId): void => {
    const policy = useSessionPolicyStore.getState().getPolicy('track', trackId);
    set(state => ({
      tracks: state.tracks.map(t => {
        if (t.id !== trackId) return t;
        const metadata: Record<string, unknown> = { ...(t.metadata ?? {}) };
        for (const key of SESSION_POLICY_TRACK_METADATA_KEYS) delete metadata[key];
        if (policy?.allowSessionOverride != null) metadata.allowSessionOverride = policy.allowSessionOverride;
        if (policy?.allowSessionItemCreation != null) metadata.allowSessionItemCreation = policy.allowSessionItemCreation;
        if (policy?.allowSessionValueCreation != null) metadata.allowSessionValueCreation = policy.allowSessionValueCreation;
        if (policy?.allowSessionValueOverride != null) metadata.allowSessionValueOverride = policy.allowSessionValueOverride;
        return {
          ...t,
          defaultTimingMode: policy?.timingMode ?? null,
          defaultEntryMode: policy?.entryMode ?? null,
          defaultFacilitatorId: policy?.defaultFacilitatorId ?? null,
          metadata: t.metadata === null && Object.keys(metadata).length === 0 ? null : metadata,
        };
      }),
    }));
  },

  /**
   * Generate the next default track name.
   * For cycle tracks: baseName from cycle.
   * For standalone tracks: pass null as cycleId.
   */
  getNextTrackName: (cycleId: string | null, baseName: string): string => {
    const existing = cycleId ? get().getTracksForCycle(cycleId) : get().getStandaloneTracks();
    if (existing.length === 0) return baseName;
    return `${baseName} Track ${existing.length + 1}`;
  },
}));

export default useTrackStore;
